import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  Alert,
  Button,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  Chip,
  IconButton,
} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNpsAccess } from './useNpsAccess';
import {
  DEV2_DEVICE_TYPE_OPTIONS,
  DEV2_ONX_BUBBLE_ID,
  defaultDev2DeviceTypeForParent,
  defaultBubbleIdForDeviceType,
  defaultInboundDeviceForProduct,
  dev2TwilioToForDeviceType,
  dev2TestingDateForSchedule,
  DEV2_TESTING_DATE_TIMEZONE,
  DEV2_DEFAULT_SOS_LAT,
  DEV2_DEFAULT_SOS_LNG,
  DEV2_INBOUND_DEVICE_OPTIONS,
  buildDev2InboundMessageBody,
  buildDev2InboundWebhookPayload,
  buildDev2InboundWebhookUrlEncoded,
} from './dev2DeviceTestConfig';
import {
  FP_API_BASE,
  DEV2_DEVICE_TEST_SCHEDULE_PATH,
  DEV2_DEVICE_TEST_SOS_PATH,
  postDev2Schedule,
  postDev2Sos,
} from './dev2DeviceTestApi';

function NavigateToHub() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/global-search', { replace: true });
  }, [navigate]);
  return null;
}

function formatJson(data) {
  if (data === undefined || data === null) return '—';
  if (typeof data === 'string') return data;
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
}

const Dev2TestingPage = ({ searchContext }) => {
  const { allowed, checked } = useNpsAccess();
  const prefill = searchContext?.dev2TestPrefill;

  const [accountNumber, setAccountNumber] = useState('');
  const [deviceType, setDeviceType] = useState('onx');
  const [bubbleId, setBubbleId] = useState(defaultBubbleIdForDeviceType('onx'));
  const [from, setFrom] = useState('');
  const [firstName, setFirstName] = useState('Ops');
  const [lastName, setLastName] = useState('Test');
  const [latitude, setLatitude] = useState(String(DEV2_DEFAULT_SOS_LAT));
  const [longitude, setLongitude] = useState(String(DEV2_DEFAULT_SOS_LNG));
  const [inboundDevice, setInboundDevice] = useState(defaultInboundDeviceForProduct('onx'));
  const [bodyAppend, setBodyAppend] = useState('');

  const [busyKey, setBusyKey] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [lastRequest, setLastRequest] = useState(null);
  const [copyOk, setCopyOk] = useState(false);
  const [error, setError] = useState(null);

  const twilioTo = useMemo(() => dev2TwilioToForDeviceType(deviceType), [deviceType]);

  useEffect(() => {
    if (!prefill) return;
    if (prefill.accountNumber) setAccountNumber(prefill.accountNumber);
    if (prefill.from) setFrom(prefill.from);
    if (prefill.firstName) setFirstName(prefill.firstName);
    if (prefill.lastName) setLastName(prefill.lastName);
    const inferred = defaultDev2DeviceTypeForParent(prefill.parent);
    setDeviceType(inferred);
    setBubbleId(defaultBubbleIdForDeviceType(inferred));
    setInboundDevice(defaultInboundDeviceForProduct(inferred));
  }, [prefill]);

  const handleDeviceTypeChange = (value) => {
    setDeviceType(value);
    setBubbleId(defaultBubbleIdForDeviceType(value));
    setInboundDevice(defaultInboundDeviceForProduct(value));
  };

  const inboundMessageBody = useMemo(
    () =>
      inboundDevice
        ? buildDev2InboundMessageBody(
            inboundDevice,
            accountNumber,
            latitude,
            longitude,
            bodyAppend
          )
        : '',
    [inboundDevice, accountNumber, latitude, longitude, bodyAppend]
  );

  const inboundWebhookPreview = useMemo(() => {
    if (!from.trim() || !inboundMessageBody) return '';
    return buildDev2InboundWebhookUrlEncoded({
      deviceType,
      from: from.trim(),
      to: twilioTo,
      preset: inboundDevice,
      accountNumber,
      latitude,
      longitude,
      bodyAppend,
    });
  }, [
    deviceType,
    from,
    twilioTo,
    inboundDevice,
    accountNumber,
    latitude,
    longitude,
    bodyAppend,
    inboundMessageBody,
  ]);

  const buildScheduleBody = useCallback(
    () => ({
      accountNumber: accountNumber.trim(),
      bubbleId: bubbleId.trim(),
      testingDate: dev2TestingDateForSchedule(),
      firstName: firstName.trim() || 'Ops',
      lastName: lastName.trim() || 'Test',
      isActive: 1,
    }),
    [accountNumber, bubbleId, firstName, lastName]
  );

  const buildInboundApiPayload = useCallback(() => {
    return buildDev2InboundWebhookPayload({
      deviceType,
      from: from.trim(),
      to: twilioTo,
      preset: inboundDevice,
      accountNumber,
      latitude,
      longitude,
      bodyAppend,
    });
  }, [
    deviceType,
    from,
    twilioTo,
    inboundDevice,
    accountNumber,
    latitude,
    longitude,
    bodyAppend,
  ]);

  const runApi = async (key, requestRecord, runner) => {
    setError(null);
    setLastResult(null);
    setCopyOk(false);
    setLastRequest(requestRecord);
    setBusyKey(key);
    try {
      const result = await runner();
      setLastResult(result);
      if (!result.ok) {
        const statusNote = result.status != null ? ` (HTTP ${result.status})` : '';
        setError(`Request failed${statusNote}. See response data below.`);
      }
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setBusyKey(null);
    }
  };

  const requireAccount = () => {
    if (!accountNumber.trim()) {
      setError('Account number is required.');
      return false;
    }
    return true;
  };

  const requireBubble = () => {
    if (!bubbleId.trim()) {
      setError(
        deviceType === 'onx'
          ? 'Device bubbleId is required (onX is usually onX App).'
          : 'Enter the device bubbleId for the OXR hardware you are testing.'
      );
      return false;
    }
    return true;
  };

  const requireTwilio = () => {
    if (!requireAccount()) return false;
    if (!from.trim()) {
      setError('Twilio From (member phone) is required.');
      return false;
    }
    if (!inboundDevice) {
      setError('Choose an inbound device sample.');
      return false;
    }
    return true;
  };

  const makeRequestRecord = (action, path, jsonBody) => ({
    action,
    method: 'POST',
    url: `${FP_API_BASE}${path}`,
    jsonBody,
  });

  const runScheduleDevice = () => {
    if (!requireBubble() || !requireAccount()) return;
    const body = buildScheduleBody();
    runApi(
      'schedule',
      makeRequestRecord('Schedule device', DEV2_DEVICE_TEST_SCHEDULE_PATH, body),
      () => postDev2Schedule(body)
    );
  };

  const runSendInbound = () => {
    if (!requireTwilio()) return;
    const body = buildInboundApiPayload();
    runApi(
      'inbound',
      makeRequestRecord('Send SOS/SMS', DEV2_DEVICE_TEST_SOS_PATH, body),
      () => postDev2Sos(body)
    );
  };

  const lastRequestText = useMemo(() => {
    if (!lastRequest) return '';
    return JSON.stringify(lastRequest, null, 2);
  }, [lastRequest]);

  const handleCopyRequest = async () => {
    if (!lastRequestText) return;
    try {
      await navigator.clipboard.writeText(lastRequestText);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2000);
    } catch {
      setError('Could not copy request to clipboard');
    }
  };

  const buttonBusy = (key) => busyKey === key;

  const bubbleHelperText =
    deviceType === 'onx'
      ? `onX device bubbleId is usually "${DEV2_ONX_BUBBLE_ID}".`
      : 'OXR: enter the device bubbleId for the unit you are testing on Dev2.';

  const responseStatus = lastResult?.status ?? lastResult?.httpStatus;

  if (!checked) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!allowed) {
    return <NavigateToHub />;
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', py: 3, px: 2 }}>
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <ScienceIcon color="primary" />
          <Typography variant="h5" component="h1">
            Dev2 device test (Ops)
          </Typography>
          <Chip label="Dev2 only" size="small" color="warning" variant="outlined" />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Schedule a device test, then simulate one inbound Twilio SOS/SMS. <strong>Product</strong> picks the
          Dev2 hook and <code>To</code> number; <strong>Device</strong> picks the message shape in{' '}
          <code>Body</code>. Optional text is appended to that body (e.g. pass/deviation <code>1</code> or{' '}
          <code>2</code>).
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {lastResult && (
          <Alert severity={lastResult.ok ? 'success' : 'warning'} sx={{ mb: 2 }}>
            {lastResult.ok ? 'Completed.' : 'Request finished with errors.'}
            {responseStatus != null && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                status {responseStatus}
              </Typography>
            )}
          </Alert>
        )}

        {lastRequest && (
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle2">
                Request payload{lastRequest.action ? ` — ${lastRequest.action}` : ''}
              </Typography>
              <IconButton
                size="small"
                onClick={handleCopyRequest}
                aria-label="Copy request payload"
                title="Copy for backend"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Stack>
            {copyOk && (
              <Typography variant="caption" color="success.main" display="block" sx={{ mb: 0.5 }}>
                Copied to clipboard
              </Typography>
            )}
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa' }}>
              <Typography
                component="pre"
                variant="caption"
                sx={{
                  m: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                }}
              >
                {lastRequestText}
              </Typography>
            </Paper>
          </Box>
        )}

        {lastResult && lastResult.data !== undefined && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Response data
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography
                component="pre"
                variant="caption"
                sx={{
                  m: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                  color: 'text.secondary',
                }}
              >
                {formatJson(lastResult.data)}
              </Typography>
            </Paper>
          </Box>
        )}

        <Stack spacing={2}>
          <TextField
            label="Account number"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            fullWidth
            required
            helperText="Used in device message templates (e.g. Garmin Account Number, onX ID)"
          />

          <FormControl fullWidth required>
            <InputLabel id="dev2-device-type-label">Product</InputLabel>
            <Select
              labelId="dev2-device-type-label"
              label="Product"
              value={deviceType}
              onChange={(e) => handleDeviceTypeChange(e.target.value)}
            >
              {DEV2_DEVICE_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Device bubbleId"
            value={bubbleId}
            onChange={(e) => setBubbleId(e.target.value)}
            fullWidth
            required
            placeholder={deviceType === 'onx' ? DEV2_ONX_BUBBLE_ID : 'Device bubbleId on Dev2'}
            helperText={`${bubbleHelperText} Schedule only.`}
          />

          <TextField
            label="Twilio From (member phone)"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            fullWidth
            placeholder="+15551234567"
            helperText="E.164 — Twilio From on inbound SOS/SMS"
          />

          <TextField
            label="Twilio To (Dev2 inbound)"
            value={twilioTo}
            fullWidth
            disabled
            helperText="Locked — onX and OXR each use a fixed Dev2 number"
          />

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2">Inbound SOS/SMS</Typography>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="First name (schedule)"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                fullWidth
              />
              <TextField
                label="Last name (schedule)"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Latitude"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                fullWidth
              />
              <TextField
                label="Longitude"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                fullWidth
              />
            </Stack>

            <FormControl fullWidth required size="small">
              <InputLabel id="dev2-inbound-device-label">Device</InputLabel>
              <Select
                labelId="dev2-inbound-device-label"
                label="Device"
                value={inboundDevice}
                onChange={(e) => setInboundDevice(e.target.value)}
              >
                {DEV2_INBOUND_DEVICE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Append to message Body (optional)"
              value={bodyAppend}
              onChange={(e) => setBodyAppend(e.target.value)}
              fullWidth
              placeholder='e.g. "1" or "2" for test reply — concatenated to end of device template'
              helperText="Added to the end of the device template with no extra separator"
            />

            {inboundMessageBody && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Twilio <code>Body</code> (device template + append):
                </Typography>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa' }}>
                  <Typography
                    component="pre"
                    variant="body2"
                    sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace' }}
                  >
                    {inboundMessageBody}
                  </Typography>
                </Paper>
              </Box>
            )}

            {inboundWebhookPreview && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Full webhook sent to fp-api (urlencoded Twilio POST):
                </Typography>
                <Paper variant="outlined" sx={{ p: 1, bgcolor: '#fafafa', maxHeight: 140, overflow: 'auto' }}>
                  <Typography
                    component="pre"
                    variant="caption"
                    sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace' }}
                  >
                    {inboundWebhookPreview}
                  </Typography>
                </Paper>
              </Box>
            )}
          </Stack>
        </Stack>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
          Schedule <code>testingDate</code> is sent as UTC ({DEV2_TESTING_DATE_TIMEZONE}) when you schedule.
        </Typography>

        <Divider sx={{ mt: 3, mb: 2 }} />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} useFlexGap>
          <Button
            variant="outlined"
            onClick={runScheduleDevice}
            disabled={buttonBusy('schedule')}
            startIcon={buttonBusy('schedule') ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ flex: 1 }}
          >
            Schedule device
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={runSendInbound}
            disabled={buttonBusy('inbound')}
            startIcon={buttonBusy('inbound') ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ flex: 1 }}
          >
            Send SOS/SMS
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default Dev2TestingPage;
